/**
 * A wall card's height before it is measured, from the card previews' own
 * height rules: a fixed or capped preview height per type in em (so it
 * grows with the wall's type size), and the aspect of covers, posters and
 * images (their recorded size when the card has it), read from the shape
 * the opened window shares (`lib/frame-card-shape.ts`), plus an HTML
 * card's title line.
 * It places the cards not mounted yet (the virtualized rest of the wall);
 * a mounted card is measured before it paints (`use-wall-measure.ts`), and
 * the cards after it are placed again (`reconcileWallLayout`). A close
 * estimate keeps those far cards near their final places.
 */
import type { FrameThumbnailTheme } from "@/api/frame-listing";
import { htmlCardTitleEm } from "@/components/shapes/html-frame/html-card-shape";
import type { FrameData } from "@/lib/frame-data";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { cardPreviewEm, frameCardShape } from "@/lib/frame-card-shape";
import type { WallNewItem } from "./use-wall-new-items";

/** The image preview's cap (`max-h-[480px]`). */
const IMAGE_MAX_HEIGHT = 480;

/**
 * The card's height at `width` with the wall's type at `fontSize` px, in
 * the app's `theme` (an HTML poster is captured per theme).
 */
export function estimateWallCardHeight(
  frame: FrameData | undefined,
  width: number,
  fontSize: number,
  theme: FrameThumbnailTheme,
): number {
  const aspect = frame && frameCardShape(frame, theme).aspect;
  if (!aspect) {
    return Math.round(cardPreviewEm(frame?.frameType) * fontSize);
  }
  const height = width / aspect;
  if (frame.frameType === "image") {
    return Math.round(Math.min(height, IMAGE_MAX_HEIGHT));
  }
  // An HTML card's title, one or two lines, sits above its cover.
  const title =
    frame.frameType === "html"
      ? htmlCardTitleEm(frameDisplayTitle(frame), width / fontSize) * fontSize
      : 0;
  return Math.round(height + title);
}

/** A new task card: the key line, a two-line title and the status, in em. */
const NEW_TASK_EM = 10.5;
/** A new thing card that says nothing learned: the glyph line and the name. */
const NEW_THING_EM = 8;
/** A line of what it learned, its gap, and the most lines it shows. */
const DESCRIPTION_LINE_EM = 1.45;
const DESCRIPTION_GAP_EM = 0.55;
const DESCRIPTION_MAX_LINES = 4;
/** Average body character width, in em, and the cards' side padding. */
const CHARACTER_EM = 0.5;
const SIDE_PADDING_EM = 2.9;

/** A new task or thing card's height at `width` (`wall-new-card.tsx`). */
export function estimateWallNewCardHeight(
  item: WallNewItem,
  width: number,
  fontSize: number,
): number {
  if (item.kind === "task") return Math.round(NEW_TASK_EM * fontSize);
  const { learned } = item.thing;
  if (!learned) return Math.round(NEW_THING_EM * fontSize);
  const perLine = Math.max(
    1,
    Math.floor((width / fontSize - SIDE_PADDING_EM) / CHARACTER_EM),
  );
  const lines = Math.min(
    Math.ceil(learned.length / perLine),
    DESCRIPTION_MAX_LINES,
  );
  return Math.round(
    (NEW_THING_EM + DESCRIPTION_GAP_EM + lines * DESCRIPTION_LINE_EM) *
      fontSize,
  );
}

/**
 * Whether two sets of new cards lay out alike: the same keys, each read the
 * same way by `estimateWallNewCardHeight` (its kind, what a thing learned).
 */
export function sameNewCardEstimates(
  previous: ReadonlyMap<string, WallNewItem>,
  next: ReadonlyMap<string, WallNewItem>,
): boolean {
  if (previous.size !== next.size) return false;
  for (const [key, item] of next) {
    const before = previous.get(key);
    if (!before || before.kind !== item.kind) return false;
    if (
      item.kind === "thing" &&
      before.kind === "thing" &&
      before.thing.learned !== item.thing.learned
    ) {
      return false;
    }
  }
  return true;
}
